/**
 * Cache Playbook Builder
 *
 * Pure constructor for the optimizer's commit payload: validates the authored
 * playbook + action plan, synthesizes storable actions through the canonical
 * assembly, aligns compiled entries to playbook order, and enforces the
 * lossless-token rule (every variable/macro the node depends on survives into
 * the playbook verbatim).
 *
 * Deliberately hash-free: the node hash is recomputed SERVER-side at commit
 * (`commit-node-cache.ts`) from the node context with this playbook applied —
 * a client-supplied hash is never trusted.
 *
 * @module optimizer/playbook
 */

import {
  cacheActionContractIssues,
  MACRO_PATTERN,
  type CachedAction,
  type PlaybookStep,
} from '@chatsdet/types';
import type { StoredCompiledEntry } from '../types.js';
import { synthesizeCachedActions, type PlannedBrowserAction } from './action-synthesizer.js';
import type { MacroResolutionLog } from '../macros/types.js';

/** Steps the optimizer marks as agent-handled at replay (non-browser effects, unverifiable locators). */
export interface PlannedGap {
  stepOrder: number;
  /** Why this step is a gap (surfaced in compilation meta + agent prompt at replay). */
  reason: string;
}

export interface CarriedCachedAction {
  /** 1-based playbook step this incumbent action continues to fulfill. */
  stepOrder: number;
  /**
   * Existing cache action copied verbatim into the new playbook. Used for
   * incumbent-copy steps so `run_code` and replay metadata survive without
   * being round-tripped through optimizer planned-action synthesis.
   */
  action: CachedAction;
}

export interface BuildCachePlaybookOptions {
  nodeId: string;
  /** Most-direct-path playbook (core actions only, tokens verbatim). */
  playbookSteps: PlaybookStep[];
  /** Browser actions fulfilling playbook steps (literal values; provenance mapped here). */
  plannedActions: PlannedBrowserAction[];
  /** Existing cached actions kept verbatim for incumbent-copy steps. */
  carriedActions?: CarriedCachedAction[];
  /** Steps without a cached action (non-browser effects, unverifiable locators). */
  gaps: PlannedGap[];
  /** Macro provenance from run records. */
  macroResolutionLog?: MacroResolutionLog;
  agentMacroResolutions?: Map<string, string>;
  /** Variable values in effect during the source run. */
  variableValues?: Record<string, string>;
  /**
   * Tokens the node's previous playbook/playbook depended on (`$var.x`,
   * `$EMAIL()`, …). The new playbook must still reference every one of them —
   * a dropped token means the optimizer inlined a run-specific value.
   */
  requiredTokens?: string[];
}

export interface CachePlaybook {
  playbookSteps: PlaybookStep[];
  actionsJson: CachedAction[];
  compiledEntriesJson: StoredCompiledEntry[];
  /** Gap reasons keyed by step order (for compilation meta / diagnostics). */
  gapReasons: Record<number, string>;
}

export class PlaybookValidationError extends Error {
  readonly _tag = 'PlaybookValidationError';
  constructor(
    message: string,
    readonly issues: string[]
  ) {
    // Issues inline in the message — agents and CLI users see the actionable
    // detail without unwrapping the error object.
    super(issues.length > 0 ? `${message}: ${issues.join('; ')}` : message);
    this.name = 'PlaybookValidationError';
  }
}

const VAR_TOKEN = /\$var\.[A-Za-z0-9_]+(?:\.[A-Za-z0-9_]+)*/g;
// Fresh instance of the shared macro pattern — module-level, hoisted out of the
// visitor. Safe with matchAll (which clones and never mutates lastIndex).
const MACRO_TOKEN = new RegExp(MACRO_PATTERN.source, 'g');

function formatContractIssue(
  prefix: string,
  issue: { path: PropertyKey[]; message: string }
): string {
  const path = issue.path.length > 0 ? `.${issue.path.map(String).join('.')}` : '';
  const message = issue.message.replace(/^Invalid input: /, '');
  const normalizedMessage = message
    ? `${message[0]!.toLowerCase()}${message.slice(1)}`
    : 'invalid action payload';
  return `${prefix}${path}: ${normalizedMessage}`;
}

function replayContractIssues(actions: readonly CachedAction[]): string[] {
  return actions.flatMap((action, position) =>
    cacheActionContractIssues(action).map((issue) =>
      formatContractIssue(`action[${position}]`, issue)
    )
  );
}

/** Collect `$var.x` and `$MACRO()` tokens appearing anywhere in a JSON value. */
export function collectTokens(value: unknown): Set<string> {
  const out = new Set<string>();
  const visit = (v: unknown): void => {
    if (typeof v === 'string') {
      for (const m of v.matchAll(VAR_TOKEN)) out.add(m[0]);
      for (const m of v.matchAll(MACRO_TOKEN)) out.add(m[0]);
    } else if (Array.isArray(v)) {
      v.forEach(visit);
    } else if (v && typeof v === 'object') {
      Object.values(v).forEach(visit);
    }
  };
  visit(value);
  return out;
}

/**
 * Build and validate the commit payload. Throws `PlaybookValidationError` when
 * the plan is structurally unsound or drops a required token — the optimizer
 * agent reads the issues and retries.
 */
export function buildCachePlaybook(opts: BuildCachePlaybookOptions): CachePlaybook {
  const issues: string[] = [];

  // ── Structural alignment ──
  const orders = opts.playbookSteps.map((s) => s.order).sort((a, b) => a - b);
  const expected = Array.from({ length: orders.length }, (_, i) => i + 1);
  if (orders.length === 0) issues.push('playbookSteps is empty');
  if (JSON.stringify(orders) !== JSON.stringify(expected)) {
    issues.push(`playbook step orders must be contiguous from 1; got [${orders.join(', ')}]`);
  }

  const plannedActionByOrder = new Map<number, PlannedBrowserAction>();
  for (const a of opts.plannedActions) {
    if (plannedActionByOrder.has(a.stepOrder)) {
      issues.push(`step ${a.stepOrder} has more than one planned action (one action per step)`);
    }
    plannedActionByOrder.set(a.stepOrder, a);
  }
  const carriedActionByOrder = new Map<number, CachedAction>();
  for (const carried of opts.carriedActions ?? []) {
    if (carriedActionByOrder.has(carried.stepOrder)) {
      issues.push(
        `step ${carried.stepOrder} has more than one carried action (one action per step)`
      );
    }
    carriedActionByOrder.set(carried.stepOrder, carried.action);
  }
  for (const order of plannedActionByOrder.keys()) {
    if (carriedActionByOrder.has(order)) {
      issues.push(`step ${order} has both a planned action and a carried action`);
    }
  }
  const gapByOrder = new Map(opts.gaps.map((g) => [g.stepOrder, g]));

  for (const step of opts.playbookSteps) {
    const hasAction = plannedActionByOrder.has(step.order) || carriedActionByOrder.has(step.order);
    const hasGap = gapByOrder.has(step.order);
    if (hasAction && hasGap) issues.push(`step ${step.order} is both a cached action and a gap`);
    if (!hasAction && !hasGap) {
      issues.push(`step ${step.order} ("${step.target}") has neither a planned action nor a gap`);
    }
  }
  for (const order of [
    ...plannedActionByOrder.keys(),
    ...carriedActionByOrder.keys(),
    ...gapByOrder.keys(),
  ]) {
    if (!opts.playbookSteps.some((s) => s.order === order)) {
      issues.push(`planned action/gap references step ${order} which is not in the playbook`);
    }
  }

  if (issues.length > 0) {
    throw new PlaybookValidationError('Playbook plan is structurally unsound', issues);
  }

  // ── Synthesis through the canonical assembly ──
  const orderedPlanned = [...plannedActionByOrder.entries()].sort((a, b) => a[0] - b[0]);
  const synthesizedPlannedActions = synthesizeCachedActions({
    nodeId: opts.nodeId,
    planned: orderedPlanned.map(([, a]) => a),
    macroResolutionLog: opts.macroResolutionLog,
    agentMacroResolutions: opts.agentMacroResolutions,
    variableValues: opts.variableValues,
  });
  const selectorlessElementActions = synthesizedPlannedActions
    .map((action, index) => ({ action, stepOrder: orderedPlanned[index]?.[0] }))
    .filter(({ action }) => action.requiresAgent);
  if (selectorlessElementActions.length > 0) {
    throw new PlaybookValidationError(
      'Playbook contains cached actions without selectors',
      selectorlessElementActions.map(({ action, stepOrder }) => {
        const orderLabel = stepOrder === undefined ? 'unknown step' : `step ${stepOrder}`;
        return `${orderLabel}: ${action.actionType} requires a selector; write a locator or mark the step as a gap`;
      })
    );
  }
  const synthesizedByOrder = new Map(
    orderedPlanned.map(([order], index) => [order, synthesizedPlannedActions[index]!] as const)
  );
  const actionsJson: CachedAction[] = [];
  const actionIndexByOrder = new Map<number, number>();
  for (const step of opts.playbookSteps.slice().sort((a, b) => a.order - b.order)) {
    const carried = carriedActionByOrder.get(step.order);
    const synthesized = synthesizedByOrder.get(step.order);
    if (!carried && !synthesized) continue;
    const actionIndex = actionsJson.length;
    actionIndexByOrder.set(step.order, actionIndex);
    actionsJson.push(carried ?? { ...synthesized!, index: actionIndex });
  }

  const actionContractIssues = replayContractIssues(actionsJson);
  if (actionContractIssues.length > 0) {
    throw new PlaybookValidationError(
      'Playbook contains actions that cannot replay',
      actionContractIssues
    );
  }

  // ── Lossless-token check ──
  // Tokens must survive into the playbook or the parameterized actions
  // (macro provenance lands as macroBindings, variable provenance as
  // variableKey/variableBindings or $var literals).
  if (opts.requiredTokens?.length) {
    const present = collectTokens({ playbook: opts.playbookSteps, actions: actionsJson });
    const bindingKeys = new Set<string>();
    for (const a of actionsJson) {
      if (a.variableKey) bindingKeys.add(`$var.${a.variableKey}`);
      for (const key of Object.values(a.variableBindings ?? {})) bindingKeys.add(`$var.${key}`);
      for (const expr of Object.values(a.macroBindings ?? {})) bindingKeys.add(expr);
      // A capture PRODUCES its variable — the strongest possible way for a
      // playbook to preserve `$var.<saveAs>`.
      if (a.actionType === 'capture' && typeof a.args?.saveAs === 'string' && a.args.saveAs) {
        bindingKeys.add(`$var.${a.args.saveAs}`);
      }
    }
    const missing = opts.requiredTokens.filter((t) => !present.has(t) && !bindingKeys.has(t));
    if (missing.length > 0) {
      throw new PlaybookValidationError(
        'Playbook drops required variable/macro tokens — run-specific values were inlined',
        missing.map((t) => `missing token: ${t}`)
      );
    }
  }

  // ── Compiled entries aligned to playbook order ──
  const gapReasons: Record<number, string> = {};
  const compiledEntriesJson: StoredCompiledEntry[] = opts.playbookSteps
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((step) => {
      const gap = gapByOrder.get(step.order);
      if (gap) {
        gapReasons[step.order] = gap.reason;
        return { type: 'gap' as const, stepOrder: step.order };
      }
      return {
        type: 'cached' as const,
        stepOrder: step.order,
        actionIndices: [actionIndexByOrder.get(step.order)!],
      };
    });

  return { playbookSteps: opts.playbookSteps, actionsJson, compiledEntriesJson, gapReasons };
}
